// Одобренные изделия РС — реестр свидетельств Регистра. ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНЫЙ ВОПРОС ЭКРАНА: «до какой даты действует одобрение». Поэтому срок — не последняя
// колонка, а бейдж рядом с номером свидетельства, и сортировка по умолчанию идёт по сроку.
//
// РАЗДЕЛ НАЗЫВАЕТСЯ «ИЗДЕЛИЯ», НО ЭТО НЕ ТОЛЬКО ИЗДЕЛИЯ. В данных 18 видов свидетельств, и
// 6 137 строк из 14 781 (на 28.07.2026) — признание ПРЕДПРИЯТИЯ, изготовителя или
// лаборатории (ССП, СП, СПИ, СПЛ, CKK), а не одобрение типа изделия. Человек, который ищет
// «одобрен ли прибор», обязан видеть разницу, иначе примет признание завода за одобрение
// прибора. Поэтому у таких строк стоит пометка, а фильтр по виду свидетельства — на
// видном месте.
//
// ДВА ПОЛЯ ПОИСКА, И ЭТО НЕ ПРИХОТЬ. Поиск по изготовителю идёт по индексу upper(manufacturer)
// с начала строки — единицы миллисекунд. Поиск по названию изделия так работать не может:
// в данных ВСЕ непустые названия начинаются с номера пункта свидетельства («1. Эжекторы
// водоструйные»), и «эжектор» с начала строки не найдётся никогда. Поэтому у названия своё
// поле с поиском внутри строки и честной оговоркой про цену (полный проход по реестру).
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useИзделиеРС, useИзделияРС, useP55Refs } from '../api/p55'
import type { ИзделиеРС, КарточкаИзделия } from '../api/p55'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtDays, fmtText, pluralN } from '../lib/format'
import {
  НЕТ_НАЗВАНИЙ_НОМЕНКЛАТУРЫ, ПУТИ, РАЗМЕР_СТРАНИЦЫ, СКОРО_ДНЕЙ, видСвидетельства, видСрока,
  классСостояния, классСрока, подписьСрока, проПредприятие, состояние,
} from '../lib/p55'
import type { ФильтрыP55 } from '../lib/p55'
import { Охват, Пейджер, Свежесть } from './RsVessels'

export default function RsProducts() {
  const { id } = useParams<{ id: string }>()
  return id ? <Карточка id={id} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [q, setQ] = useState('')
  const [name, setName] = useState('')
  const [certType, setCertType] = useState('')
  const [country, setCountry] = useState('')
  const [nomen, setNomen] = useState('')
  const [valid, setValid] = useState('')
  const [sort, setSort] = useState('valid_desc')
  const [start, setStart] = useState(0)

  const refs = useP55Refs()
  const счёт = refs.data?.counts
  const сроки = refs.data?.validity
  const порог = refs.data?.expiring_days

  const фильтры = useMemo<ФильтрыP55>(() => ({
    q: q.trim() || undefined,
    name: name.trim() || undefined,
    cert_type: certType || undefined,
    country: country || undefined,
    nomen: nomen || undefined,
    valid: valid || undefined,
    sort,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, name, certType, country, nomen, valid, sort, start])

  const запрос = useИзделияРС(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="page-head">
        <h1>Одобренные изделия</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · свидетельств в реестре: ${счёт.products}` : ''}
        </span>
      </div>

      <Охват scope={данные?._scope} />

      <div className="notice">
        <b>Это реестр свидетельств, а не каталог приборов.</b> Наряду с одобрением типа изделия
        (СТО) здесь лежат свидетельства о соответствии предприятия, о признании изготовителя и
        лаборатории — они говорят про организацию, а не про прибор. Такие строки помечены;
        отобрать нужное можно фильтром «вид свидетельства».
        <div className="hint" style={{ marginTop: 6 }}>
          Название изделия заполнено в{' '}
          {refs.data ? refs.data.filled_products.product_name : '—'} строках из{' '}
          {счёт?.products ?? '—'}: у свидетельств о признании предприятия названия изделия нет
          по существу. У таких строк смотрите изготовителя.
        </div>
      </div>

      {сроки ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="tiles">
            <ПлиткаСрока подпись="Действуют" число={сроки.products_active} класс=""
                         пояснение={`ещё больше ${fmtDays(порог ?? СКОРО_ДНЕЙ)}`}
                         onClick={() => { setStart(0); setValid('active') }} />
            <ПлиткаСрока подпись="Истекают" число={сроки.products_expiring} класс="hot"
                         пояснение={`в пределах ${fmtDays(порог ?? СКОРО_ДНЕЙ)}`}
                         onClick={() => { setStart(0); setValid('expiring') }} />
            <ПлиткаСрока подпись="Истекли" число={сроки.products_expired} класс=""
                         пояснение="срок прошёл"
                         onClick={() => { setStart(0); setValid('expired') }} />
            <ПлиткаСрока подпись="Без срока в данных" число={сроки.products_no_date} класс=""
                         пояснение="скрапер срок не разобрал — это не «бессрочно»"
                         onClick={() => { setStart(0); setValid('none') }} />
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Пустой срок — <b>не «действует бессрочно»</b>. Это значит, что срока в данных нет,
            и опираться на такую строку без сверки с реестром РС нельзя.
          </div>
        </div>
      ) : null}

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="rs-products">
          <div className="f" style={{ flex: 2, minWidth: 250 }}>
            <label htmlFor="rpq">Изготовитель, № свидетельства или код номенклатуры</label>
            <input id="rpq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: FURUNO, 23.44.01.06393.120 или 22000000" />
          </div>

          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="rpname">Название изделия (внутри строки)</label>
            <input id="rpname" value={name} onChange={(e) => сСброса(setName)(e.target.value)}
                   placeholder="например: эжектор, АРБ, дверь" />
          </div>

          <div className="f" style={{ minWidth: 240 }}>
            <label htmlFor="rptype">Вид свидетельства</label>
            <select id="rptype" value={certType}
                    onChange={(e) => сСброса(setCertType)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.product_cert_type ?? []).map((t) => {
                const в = видСвидетельства(t.k)
                return (
                  <option key={t.k} value={t.k} title={в.title}>
                    {в.code} — {в.title.slice(0, 40)} ({t.n})
                  </option>
                )
              })}
            </select>
          </div>

          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="rpcountry">Страна изготовителя</label>
            <select id="rpcountry" value={country}
                    onChange={(e) => сСброса(setCountry)(e.target.value)}>
              <option value="">любая</option>
              {(refs.data?.product_country ?? []).map((c) => (
                <option key={c.k} value={c.k}>{c.k || '(не указана)'} ({c.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 160 }}>
            <label htmlFor="rpnomen">Код номенклатуры РС</label>
            <select id="rpnomen" value={nomen} onChange={(e) => сСброса(setNomen)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.product_nomen ?? []).map((n) => (
                <option key={n.k} value={n.k}>{n.k || '(нет кода)'} ({n.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="rpvalid">Срок действия</label>
            <select id="rpvalid" value={valid} onChange={(e) => сСброса(setValid)(e.target.value)}>
              <option value="">любой</option>
              <option value="valid">действует сегодня</option>
              <option value="expiring">истекает ({fmtDays(порог ?? СКОРО_ДНЕЙ)})</option>
              <option value="active">действует дольше</option>
              <option value="expired">истёк</option>
              <option value="none">срока нет в данных</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="rpsort">Сортировка</label>
            <select id="rpsort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="valid_desc">срок: сначала дальние</option>
              <option value="valid">срок: сначала ближние</option>
              <option value="mfr">по изготовителю</option>
              <option value="name">по названию изделия</option>
              <option value="cert">по № свидетельства</option>
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          {nomen
            ? `${НЕТ_НАЗВАНИЙ_НОМЕНКЛАТУРЫ}: код ${nomen} показан как есть. `
            : ''}
          Поиск по <b>изготовителю</b> идёт с начала строки и по индексу — он быстрый. Поиск по{' '}
          <b>названию изделия</b> — внутри строки, полным проходом: иначе он не работал бы вовсе,
          потому что в данных все названия начинаются с номера пункта («1. Эжекторы водоструйные»).
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">№ свидетельства</th>
                  <th className="nowrap">Действует до</th>
                  <th>Изделие</th>
                  <th>Изготовитель</th>
                  <th className="nowrap">Страна</th>
                  <th className="nowrap">Вид</th>
                  <th className="nowrap">Состояние</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((p) => (
                  <Строка key={p.src_id} p={p} порог={порог} />
                ))}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty">
                        <div className="empty-title">Под эти условия свидетельств нет</div>
                        <div className="empty-hint">
                          {q.trim()
                            ? 'Поиск по изготовителю идёт с начала строки: «Юг» найдёт'
                              + ' «Югдизельмаш», а «дизель» — нет. Название изделия ищите'
                              + ' во втором поле.'
                            : 'Регистр называет изделия своими словами: радар — это'
                              + ' «радиолокационная станция», АРБ — «аварийный радиобуй».'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching} onStart={setStart} />
          <Свежесть refs={refs.data} />
        </div>
      </QueryState>
    </>
  )
}

function ПлиткаСрока({ подпись, число, пояснение, класс, onClick }: {
  подпись: string
  число: number
  пояснение: string
  класс: '' | 'hot'
  onClick: () => void
}) {
  // Число — текстом, как в остальных плитках CRM (дебиторка, «Коротко» у клиента): кнопка
  // на его месте потеряла бы крупное начертание и в ряду плиток читалась бы как ссылка.
  // Отбор — отдельной мелкой кнопкой под числом.
  return (
    <div className={`tile ${класс}`}>
      <div className="t-label">{подпись}</div>
      <div className="t-sum num">{число}</div>
      <div className="t-label">
        {пояснение}{' '}
        <button onClick={onClick} title="показать только эти свидетельства">отобрать</button>
      </div>
    </div>
  )
}

function Строка({ p, порог }: { p: ИзделиеРС; порог: number | undefined }) {
  const с = состояние(p.status)
  const вид = видСвидетельства(p.certificate_type)
  const срок = видСрока(p.days_left, порог)
  return (
    <tr>
      <td className="mono nowrap" data-label="№ свидетельства">
        <Link to={ПУТИ.изделие(p.src_id)}>{fmtText(p.certificate_no)}</Link>
      </td>
      <td className="nowrap" data-label="Действует до">
        {fmtDate(p.valid_to)}
        <div>
          <span className={`badge ${классСрока(срок)}`}>{подписьСрока(p.days_left, порог)}</span>
        </div>
      </td>
      <td data-label="Изделие">
        {p.product_name
          ? p.product_name
          : (
            <span className="muted">
              названия нет в данных
              {проПредприятие(p.certificate_type) ? ' — свидетельство про предприятие' : ''}
            </span>
          )}
        {p.nomen_code ? <div className="hint mono">номенклатура {p.nomen_code}</div> : null}
      </td>
      <td data-label="Изготовитель">{fmtText(p.manufacturer)}</td>
      <td className="nowrap" data-label="Страна">{fmtText(p.manufacturer_country)}</td>
      <td className="nowrap" data-label="Вид">
        <span className="badge" title={вид.title}>{вид.code}</span>
        {проПредприятие(p.certificate_type) && (
          <div className="hint">про предприятие</div>
        )}
      </td>
      <td className="nowrap" data-label="Состояние">
        <span className={`badge ${классСостояния(с.вид)}`}>{с.вид}</span>
        {с.хвост ? <div className="hint">{с.хвост}</div> : null}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ id }: { id: string }) {
  const запрос = useИзделиеРС(id)
  const refs = useP55Refs()
  const p = запрос.data
  const порог = refs.data?.expiring_days

  return (
    <>
      <div className="page-head">
        <h1>Свидетельство {p?.certificate_no || `#${id.slice(0, 8)}`}</h1>
        <div className="spacer" />
        <Link to={ПУТИ.изделия}>← к реестру изделий</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {p && (
          <div className="grid-2">
            <div>
              <Основное p={p} порог={порог} />
              <ДругиеСвидетельства p={p} порог={порог} />
            </div>
            <div>
              <Изготовитель p={p} />
            </div>
          </div>
        )}
      </QueryState>
    </>
  )
}

function Основное({ p, порог }: { p: КарточкаИзделия; порог: number | undefined }) {
  const вид = видСвидетельства(p.certificate_type)
  const с = состояние(p.status)
  const срок = видСрока(p.days_left, порог)
  return (
    <div className="card">
      <h2>{p.product_name || вид.title}</h2>
      <dl className="dl">
        <dt>Действует до</dt>
        <dd>
          {fmtDate(p.valid_to)}{' '}
          <span className={`badge ${классСрока(срок)}`}>{подписьСрока(p.days_left, порог)}</span>
          {p.valid_to === null && (
            <div className="hint">
              Срока в данных нет — это не «бессрочно». Сверьтесь с реестром РС по ссылке ниже.
            </div>
          )}
        </dd>
        <dt>Выдано</dt><dd>{fmtDate(p.valid_from)}</dd>
        <dt>Состояние по данным РС</dt>
        <dd>
          <span className={`badge ${классСостояния(с.вид)}`}>{с.вид}</span>
          {с.хвост ? <div className="hint">{с.хвост}</div> : null}
        </dd>
        <dt>Номер</dt><dd className="mono">{fmtText(p.certificate_no)}</dd>
        <dt>Вид свидетельства</dt>
        <dd>
          {вид.title} <span className="muted mono">({вид.code})</span>
          {проПредприятие(p.certificate_type) && (
            <div className="notice" style={{ marginTop: 6, marginBottom: 0 }}>
              <b>Это свидетельство про организацию, а не про изделие.</b> Оно означает, что
              Регистр признал предприятие (изготовителя, лабораторию), и не означает, что
              одобрен какой-то конкретный прибор.
            </div>
          )}
        </dd>
        <dt>Изготовитель</dt><dd>{fmtText(p.manufacturer)}</dd>
        <dt>Страна</dt><dd>{fmtText(p.manufacturer_country)}</dd>
        <dt>Номенклатура РС</dt>
        <dd>
          <span className="mono">{fmtText(p.nomen_code)}</span>
          <div className="hint">{НЕТ_НАЗВАНИЙ_НОМЕНКЛАТУРЫ}</div>
        </dd>
        <dt>Документ</dt>
        <dd>
          {p.doc_url
            ? <a href={p.doc_url} target="_blank" rel="noreferrer">открыть в реестре РС ↗</a>
            : <span className="muted">ссылки в данных нет</span>}
          <div className="hint">Сам документ остаётся на сайте Регистра: в ядре только ссылка.</div>
        </dd>
        <dt>Впервые увидено / обойдено</dt>
        <dd className="muted">{fmtDate(p.first_seen)} · {fmtDate(p.scraped_at)}</dd>
      </dl>
    </div>
  )
}

/**
 * Сколько свидетельств одного изготовителя отдаёт ядро: `LIMIT 100` в p55_api.карточку
 * изделия. Параметра у ручки нет, попросить больше нельзя.
 *
 * Раньше «до 100» стояло словами рядом с настоящим числом строк, и подпись «37 (показано
 * до 100)» читалась как «а всего их может быть сто». Теперь оговорка появляется, только
 * когда список и правда упёрся в предел, а число в ней — из той же константы, что и
 * сравнение: разойтись им негде.
 */
const СВИДЕТЕЛЬСТВ_ИЗГОТОВИТЕЛЯ = 100

function ДругиеСвидетельства({ p, порог }: { p: КарточкаИзделия; порог: number | undefined }) {
  if (p.same_manufacturer.length === 0) {
    return (
      <div className="card">
        <h2>Другие свидетельства этого изготовителя</h2>
        <div className="empty">
          <div className="empty-title">
            Больше свидетельств у «{fmtText(p.manufacturer)}» в реестре нет.
          </div>
          <div className="empty-hint">
            Совпадение по названию точное — Регистр пишет название сам, и «похожих» мы
            не подбираем.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Другие свидетельства этого изготовителя</h2>
        <div className="spacer" />
        <span className="muted">
          {pluralN(p.same_manufacturer.length, 'свидетельство', 'свидетельства', 'свидетельств')}
          {p.same_manufacturer.length >= СВИДЕТЕЛЬСТВ_ИЗГОТОВИТЕЛЯ
            && ` — больше ${СВИДЕТЕЛЬСТВ_ИЗГОТОВИТЕЛЯ} ядро не отдаёт`}
        </span>
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">№</th>
              <th className="nowrap">До</th>
              <th>Изделие</th>
              <th className="nowrap">Вид</th>
            </tr>
          </thead>
          <tbody>
            {p.same_manufacturer.map((s) => {
              const срок = видСрока(s.days_left, порог)
              const вид = видСвидетельства(s.certificate_type)
              return (
                <tr key={s.src_id}>
                  <td className="mono nowrap" data-label="№">
                    <Link to={ПУТИ.изделие(s.src_id)}>{fmtText(s.certificate_no)}</Link>
                  </td>
                  <td className="nowrap" data-label="До">
                    {fmtDate(s.valid_to)}
                    <div><span className={`badge ${классСрока(срок)}`}>{срок}</span></div>
                  </td>
                  <td data-label="Изделие">
                    {s.product_name || <span className="muted">названия нет</span>}
                  </td>
                  <td className="nowrap" data-label="Вид">
                    <span className="badge" title={вид.title}>{вид.code}</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Изготовитель({ p }: { p: КарточкаИзделия }) {
  return (
    <>
      <div className="card">
        <h2>Признан ли изготовитель сервисником</h2>
        {p.as_supplier.length === 0 ? (
          <div className="empty">
            <div className="empty-title">
              В реестре признанных сервисных организаций «{fmtText(p.manufacturer)}» нет.
            </div>
            <div className="empty-hint">
              Это обычно и значит: изделие одобрено, а обслуживать его вправе другие компании —
              смотрите раздел «Признанные сервисники РС».
            </div>
          </div>
        ) : (
          <>
            <ul className="plain-list">
              {p.as_supplier.map((s, i) => (
                <li key={`${s.certificate_no}-${i}`}>
                  <Link to={ПУТИ.сервисник(s.company)}>{s.company}</Link>
                  <div className="hint">
                    {fmtText(s.city)}, {fmtText(s.country)} · свидетельство{' '}
                    <span className="mono">{fmtText(s.certificate_no)}</span> до{' '}
                    {fmtDate(s.valid_to)}
                  </div>
                </li>
              ))}
            </ul>
            <div className="hint" style={{ marginTop: 8 }}>
              Тот же изготовитель есть и в реестре сервисников: значит Регистр признал его не
              только как производителя, но и как предприятие, выполняющее работы.
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Он же у нас в CRM</h2>
        {p.our_company.length === 0 ? (
          <div className="empty">
            <div className="empty-title">
              Компании с точно таким названием в наших компаниях нет.
            </div>
            <div className="empty-hint">
              Это не значит, что мы с ней не работаем: Регистр пишет полное юридическое имя
              («Общество с ограниченной ответственностью …»), а у нас в карточке чаще
              сокращённое. Сравнение здесь только точное — «похожее» подсунуло бы чужую компанию.
            </div>
          </div>
        ) : (
          <ul className="plain-list">
            {p.our_company.map((c) => (
              <li key={c.bitrix_id}>
                <EntityLink kind="company" id={c.bitrix_id} title={c.title} />
                <div className="hint">{fmtText(c.company_type)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
